import React, { createContext, useContext, useEffect, useState } from "react";
import "./NewPortalLayout.css";
import ProgressBar from "../../features/salescodeStudio/progressBar/progressBar";
import ProjectConfigurationLayout from "../../features/salescodeStudio/mandatoryConfigs/projectSetupConfigLayout";
import {
  getOnboardingConfig,
  updateOnboardingStep,
  updateProgressBarPoint,
} from "../../services/userOnboardService/userOnboardService";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  getAccessToken,
  getLob,
  logoutUser,
} from "../../services/authenticationService";
import * as Sentry from "@sentry/react";
import {
  doFinalConfigSubmit,
  fetchAndSetUiConfigOptions,
  fetchCurrentLobCountry,
  getMetaDataConfig,
  getUiConfigOptions,
  validateMetaDataResponse,
} from "../../services/uiConfigService";
import { otherConfigOptionsAttributes } from "../../features/salescodeStudio/mandatoryConfigs/configSetupScreen/widget/configBottomBar";
import FeatureSelectionScreen from "../../features/salescodeStudio/feautreSelection/screen/feautreSelectionScreen";
import { configurationAttributeType } from "../../types";
import { metaDataBatchPayload } from "../../types/types";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { META_DATA_BATCH_API } from "../../services/networkService";
import { manageUpdateAccessObj } from "../../features/manageUpdateAccess/manageUpdateAccessTypes";
import { updateConfigRequestBody } from "../../services/managePoliciesService";
import DataUploadScreenLayout from "../dataUploadLayout/dataUploadScreenLayout";
import ProductionMovementScreen from "../../features/salescodeStudio/finalApp/screens/productionMovementScreen";
import { setCurrentRole } from "../../stateManagement/actions/roleActions";
import { useSelector } from "react-redux";
import { OptionType } from "../../features/manageHomepage/ManageHomePageTypes";
import store from "../../../src/stateManagement/store";
import OptionalFeatureLayout from "../../features/salescodeStudio/mandatoryConfigs/optionalFeatures/OptionalFeatureLayout";
import { useUiConfigOption } from "../../providers/UiConfigOptionProvider";
import { PopupContextType, PopupProvider } from "./PopUpContext";
import {
  FeatureSelectionOptionsContext
} from "../../features/settings/components/FeatureSelectionOptionsContext";

interface ConfigOptionsContextType {
  optional: string | null;
  setOptional: React.Dispatch<React.SetStateAction<string | null>>;
  uiConfigOptionsProp: otherConfigOptionsAttributes[];
  setUiConfigOptionsProp: React.Dispatch<
    React.SetStateAction<otherConfigOptionsAttributes[]>
  >;
}
export const ConfigOptionsContext = createContext<
  ConfigOptionsContextType | undefined
>(undefined);



interface PlaygroundConfigValueProps {
  playgroundConfiguration: configurationAttributeType[];
  setPlayGroundConfiguration: React.Dispatch<
    React.SetStateAction<configurationAttributeType[]>
  >;
  onTriggerConfigChanges: () => Promise<boolean>;
  keyValue: number;
  cleanSyncDataLoaderIds: string[];
  setCleanSyncDataLoaderIds: React.Dispatch<React.SetStateAction<string[]>>;
  setKeyValue: React.Dispatch<React.SetStateAction<number>>;
}

export const PlaygroundConfigValueContext = createContext<
  PlaygroundConfigValueProps | undefined
>(undefined);

const defaultRole: OptionType = {
  id: "salesrep",
  label: "SalesRep",
};

function NewPortalLayout(props: {
  children?: React.ReactNode;
  disableHeader?: boolean;
}) {
  const [playgroundConfiguration, setPlayGroundConfiguration] = useState<
    configurationAttributeType[]
  >([]);
  const [progressBarPoints, setProgressBarPoints] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [optional, setOptional] = useState<string | null>(null);
  const savedRole: any = useSelector(() => store.getState().roleState.role);
  const [role, setRole] = useState<OptionType>(
    !savedRole?.id ? defaultRole : savedRole
  );
  const { activeOption } = useUiConfigOption();
  const [uiConfigOptionsProp, setUiConfigOptionsProp] =
    useState<otherConfigOptionsAttributes[]>([]);
  const [keyValue, setKeyValue] = useState<number>(0);
  const [cleanSyncDataLoaderIds, setCleanSyncDataLoaderIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/onboarding") {
      setOptional(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    handlePointsStatusUpdate();
  }, [progressBarPoints]);

  const init = async () => {
    const result = await fetchAndSetUiConfigOptions();
    setUiConfigOptionsProp(result);
  }

  const handlePointsStatusUpdate = async () => {
    if (progressBarPoints === 0) return; // so that intitally it only gets from setValue api not from update function
    await updateProgressBarPoint(progressBarPoints);
  };
  async function setAppSubscription(): Promise<string | undefined> {
    try {
      const portalConfig = await getMetaDataConfig(
        "clientConfig",
        "portal_configuration"
      );
      const portal_config: configurationAttributeType[] =
        portalConfig?.domainValues ?? [];
  
      const subscriptionType = portal_config?.find(
        (item) => item.name === "subscriptionType"
      )?.value ?? undefined; 
  
      if (subscriptionType) {
        localStorage.setItem("subscriptionType", subscriptionType); 
      }
  
      return subscriptionType;
    } catch (err) {
      console.error("Error fetching subscriptionType:", err);
      return undefined;
    }
  }

  useEffect(() => {
    const getOnboardingStep = async () => {
      const isLoggedIn = getAccessToken();
      if (!isLoggedIn) {
        await logoutUser();
        return;
      }

      const onboardingConfig = await getOnboardingConfig();
      if (
        (!onboardingConfig || !onboardingConfig.isOnboarding) &&
        !props.disableHeader
      ) {
        navigate("/dashboard");
        return;
      }

      setProgressBarPoints(onboardingConfig.mandatoryConfigSetupProgress);

      const onboardingStepFromLocalStorage =
        window.localStorage.getItem("onboarding");
      // if (!onboardingStepFromLocalStorage) {
      //   window.localStorage.setItem(
      //     "onboarding",
      //     onboardingConfig.step.toString()
      //   );
      //   setCurrentStep(onboardingConfig.step - 1);
      // } else {
      //   setCurrentStep(parseInt(onboardingStepFromLocalStorage) - 1);
      // }

      const completionTrackObject: { [k: number]: boolean } = {};
      const step: number = onboardingStepFromLocalStorage
        ? parseInt(onboardingStepFromLocalStorage)
        : onboardingConfig.step;
      for (let i = 0; i < step - 1; i++) {
        completionTrackObject[i] = true;
      }

      setCompleted(completionTrackObject);
    };
    fetchCurrentLobCountry();
    localStorage.setItem("projectConfigItem", "{}");
    localStorage.setItem("themeConfigItem", "{}");

    getOnboardingStep();
    setAppSubscription();
    // TODO : only change role if it is not the same as the savedRole and do not use defaultRole
    setCurrentRole(!savedRole?.id ? defaultRole : savedRole);
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTriggerConfigChanges = async () => {
    return await doFinalConfigSubmit(
      "playground_config",
      playgroundConfiguration
    );
  };

  const handleStepIconClick = (stepNumber: number) => {
    if (stepNumber === 0) {
      return;
    }

    if (!completed[stepNumber]) {
      updateOnboardingStep(stepNumber + 1);
      window.localStorage.setItem("onboarding", (stepNumber + 1).toString());
    }

    localStorage.setItem("projectConfigItem", "{}");
    localStorage.setItem("themeConfigItem", "{}");
    setCurrentStep(stepNumber);
  };

  const handleNextStep = () => {
    setCompleted((prev) => ({
      ...prev,
      [currentStep]: true,
    }));
    localStorage.setItem("projectConfigItem", "{}");
    localStorage.setItem("themeConfigItem", "{}");

    // Move to the next step
    handleStepIconClick(currentStep + 1);
  };

  const renderCurrentTab = (tab: number) => {
    if (tab === 0) {
      setCurrentStep(1); // Jump to step 2 if tab is 0
      return null;
    }
    switch (tab) {
      case 1:
        return (
          <FeatureSelectionScreen
            handleStepIconClick={handleStepIconClick}
            currentStep={1}
            completed={completed}
            setCompleted={setCompleted}
          />
        );

      case 2:
        return (
          // <ProjectConfigurationLayout
          //   handleStepIconClick={handleStepIconClick}
          //   currentStep={2}
          //   completed={completed}
          //   setCompleted={setCompleted}
          //   progressBarPoints={progressBarPoints}
          //   setProgressBarPoints={setProgressBarPoints}
          // />
          <div style={{ height: "100%" }}>
            <OptionalFeatureLayout disableBack={false} handleStepIconClick={handleStepIconClick}
            setCompleted={setCompleted} currentStep={2} newPortal={true}>
              {" "}
              {activeOption.targetComponent}
            </OptionalFeatureLayout>
          </div>
        );

      case 3:
        return (
          <DataUploadScreenLayout
            handleStepIconClick={handleStepIconClick}
            currentStep={3}
            completed={completed}
            setCompleted={setCompleted}
          />
        );

      case 4:
        return (
          <ProductionMovementScreen handleStepIconClick={handleStepIconClick} />
        );

      default:
        return (
          <button onClick={handleNextStep} disabled={currentStep === 0}>
            Next Screen
          </button>
        );
    }
  };

  return (
    <PlaygroundConfigValueContext.Provider
      value={{
        playgroundConfiguration,
        setPlayGroundConfiguration,
        onTriggerConfigChanges,
        keyValue,
        setKeyValue,
        cleanSyncDataLoaderIds,
        setCleanSyncDataLoaderIds,
      }}
    >
      <FeatureSelectionOptionsContext.Provider
        value={{ uiConfigOptionsProp, setUiConfigOptionsProp }}
      >
        <ConfigOptionsContext.Provider
          value={{
            optional,
            setOptional,
            uiConfigOptionsProp,
            setUiConfigOptionsProp,
          }}
        >
        <PopupProvider>
          <div className={`newPortalLayoutContainer`}>
            {props.disableHeader ? (
              <></>
            ) : (
              <ProgressBar
                optional={optional}
                currentStep={currentStep}
                handleStepIconClick={handleStepIconClick}
                completed={completed}
                setCompleted={setCompleted}
              />
            )}
            <div
              className={`newPortalLayoutInnerComponentContainer ${
                props.disableHeader === true ? `active-options` : ""
              }`}
            >
              <div className="newPortalLayoutInnerComponent">
                {props.children ? (
                  <>{props.children}</>
                ) : (
                  <> {renderCurrentTab(currentStep)}</>
                )}
              </div>
            </div>
          </div>
        </PopupProvider>
        </ConfigOptionsContext.Provider>
      </FeatureSelectionOptionsContext.Provider>
    </PlaygroundConfigValueContext.Provider>
  );
}

export default NewPortalLayout;
